import { useEffect } from 'react';
import { create } from 'zustand';
import type OlMap from 'ol/Map';
import type BaseLayer from 'ol/layer/Base';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import { useMapStore } from '@/store/map.store';
import layerStylesService from '@/services/layerStyles.service';

/**
 * A person's own views of our (platform) layers on the map. GeoServer draws these layers; the server sends it the
 * person's view as an SLD it makes itself (GeoServerProxy), so all the map has to do is say which version of the view
 * it wants (`GXV`, so a changed view is not answered from the browser's cache) and, while a look is being tried in the
 * style editor, the token of that look (`GXPREVIEW`). Neither is passed on to GeoServer.
 */

interface ViewsState {
  /** The version of each view the person keeps, by our layer's workspace:name in lower case. */
  versions: Record<string, string>;
  canEditPlatformMain: boolean;
  loaded: boolean;
  load: () => Promise<void>;
  setVersion: (ref: string, version: string | null) => void;
}

const norm = (ref: string) => ref.toLowerCase();

export const usePlatformViews = create<ViewsState>((set) => ({
  versions: {},
  canEditPlatformMain: false,
  loaded: false,
  load: async () => {
    try {
      const mine = await layerStylesService.mine();
      const versions: Record<string, string> = {};
      for (const v of mine.views) if (v.kind === 'platform') versions[norm(v.ref)] = String(Date.parse(v.updatedAt) || 1);
      set({ versions, canEditPlatformMain: mine.canEditPlatformMain, loaded: true });
    } catch {
      set({ loaded: true });
    }
  },
  setVersion: (ref, version) => set((s) => {
    const versions = { ...s.versions };
    if (version) versions[norm(ref)] = version;
    else delete versions[norm(ref)];
    return { versions };
  }),
}));

type WmsSource = ImageWMS | TileWMS;

function wmsOf(layer: BaseLayer): WmsSource | null {
  const src = (layer as any).getSource?.();
  return src instanceof ImageWMS || src instanceof TileWMS ? src : null;
}

/** Our layers on the map by workspace:name, as the map asks GeoServer for them (LAYERS). */
export function platformSources(map: OlMap | null, ref: string): WmsSource[] {
  const out: WmsSource[] = [];
  if (!map) return out;
  const want = norm(ref);
  const bare = want.includes(':') ? want.substring(want.indexOf(':') + 1) : want;
  map.getLayers().forEach((l) => {
    const src = wmsOf(l);
    const asked = String(src?.getParams()?.LAYERS ?? '').toLowerCase();
    if (src && (asked === want || asked === bare)) out.push(src);
  });
  return out;
}

function apply(map: OlMap, versions: Record<string, string>): void {
  map.getLayers().forEach((l) => {
    const src = wmsOf(l);
    if (!src) return;
    const asked = String(src.getParams()?.LAYERS ?? '').toLowerCase();
    if (!asked || asked.includes(',')) return;
    const version = versions[asked] ?? (asked.includes(':') ? undefined : Object.entries(versions).find(([k]) => k.endsWith(`:${asked}`))?.[1]);
    // a view dropped is asked for as "main", so that the image cached for the view is not shown again
    const next = version ?? (src.getParams()?.GXV ? 'main' : undefined);
    if (src.getParams()?.GXV !== next) src.updateParams({ GXV: next });
  });
}

/** The look being tried for one of ours, or none (null): the map redraws it at once. */
export function setPlatformPreview(map: OlMap | null, ref: string, token: string | null): void {
  for (const src of platformSources(map, ref)) src.updateParams({ GXPREVIEW: token ?? undefined });
}

/** A view of one of ours saved (its version) or dropped (null): the map redraws it. */
export function platformViewChanged(map: OlMap | null, ref: string, version: string | null): void {
  usePlatformViews.getState().setVersion(ref, version);
  for (const src of platformSources(map, ref)) src.updateParams({ GXPREVIEW: undefined, GXV: version ?? 'main' });
}

/** The extra parameters a legend of one of ours asks with, so that it shows the look the map shows. */
export function legendParams(src: WmsSource | null | undefined): string {
  const p = src?.getParams?.() ?? {};
  return `${p.GXV ? `&GXV=${encodeURIComponent(p.GXV)}` : ''}${p.GXPREVIEW ? `&GXPREVIEW=${encodeURIComponent(p.GXPREVIEW)}` : ''}`;
}

/** Keeps our layers on the map drawn with the person's own views: mounted once with the map. */
export function usePlatformLayerViews(): void {
  const map = useMapStore((s) => s.mapInstance);
  const versions = usePlatformViews((s) => s.versions);
  const loaded = usePlatformViews((s) => s.loaded);
  const load = usePlatformViews((s) => s.load);
  useEffect(() => {
    if (map && !loaded) load();
  }, [map, loaded, load]);
  useEffect(() => {
    if (!map) return;
    apply(map, versions);
    const layers = map.getLayers();
    const onAdd = () => apply(map, usePlatformViews.getState().versions);
    layers.on('add', onAdd);
    return () => layers.un('add', onAdd);
  }, [map, versions]);
}
